import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { api, getTenantSlug } from '../config/api';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  BuildingLibraryIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

interface AssignBookModalProps {
  institutionId: string;
  // Pass exactly one of `groupId` or `userId`. The picker UI is identical
  // in both modes; only the POST endpoint differs. groupId → group
  // assignment, userId → individual student assignment.
  groupId?: string;
  userId?: string;
  // Friendly label for the assignment target — shown in the modal header
  // so the assigner sees who/what they're assigning to. Optional; falls
  // back to a generic title when absent.
  targetLabel?: string;
  onClose: () => void;
  onAssigned: () => void;
}

interface BookOption {
  id: string;
  title: string;
  author: string;
  cover_url: string | null;
  // Used to bucket the book into "Our Books" vs "Public Catalog":
  //   institution_id === institutionId  → owned by this institute
  //   institution_id === null            → truly public canonical
  // The endpoint /api/institutions/{id}/books returns both kinds.
  institution_id: string | null;
}

type Tab = 'institute' | 'public';

// Frontend feature flag — when false (default), public-catalog rows are
// shown but not selectable (with a tooltip explaining why). Flipping it
// to true allows institute admins to assign canonical platform books to
// their groups directly. Future v1.x change can also enforce this on the
// backend; for now it's a pure UX gate since the assign endpoint trusts
// the frontend's caller-role check.
const ALLOW_PUBLIC_BOOKS =
  process.env.REACT_APP_INSTITUTE_CAN_USE_PUBLIC_BOOKS === 'true';

const AssignBookModal: React.FC<AssignBookModalProps> = ({
  institutionId,
  groupId,
  userId,
  targetLabel,
  onClose,
  onAssigned,
}) => {
  const mode: 'group' | 'individual' = userId ? 'individual' : 'group';
  const { isDarkMode } = useTheme();
  const [books, setBooks] = useState<BookOption[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('institute');

  // Whether we render the tab strip at all. On a tenant subdomain the
  // /institutions/{id}/books endpoint returns both buckets, so we tab.
  // On platform context (rare for this modal but possible for cross-tenant
  // platform-admin use), the legacy /api/books/ endpoint is flat and we
  // skip tabs entirely.
  const tenantSlug = getTenantSlug();
  const showTabs = Boolean(tenantSlug);

  useEffect(() => {
    fetchBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBooks = async () => {
    const url = tenantSlug
      ? `/api/institutions/${institutionId}/books`
      : '/api/books/';
    try {
      const response = await api.get(url);
      setBooks(
        response.data.map((b: any) => ({
          id: b.id,
          title: b.title,
          author: b.author,
          cover_url: b.cover_url,
          institution_id: b.institution_id ?? null,
        })),
      );
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (bookId: string) => {
    setAssigning(bookId);
    try {
      const url = mode === 'individual'
        ? `/api/institutions/${institutionId}/students/${userId}/books`
        : `/api/institutions/${institutionId}/groups/${groupId}/books`;
      await api.post(url, { book_id: bookId });
      onAssigned();
      onClose();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to assign book');
    } finally {
      setAssigning(null);
    }
  };

  // Bucket all books once (memoized), then apply the search filter on the
  // active tab's bucket. Counts in the tab labels show pre-search totals so
  // the operator knows what's available even when the search filter is on.
  const { instituteBooks, publicBooks } = useMemo(() => {
    const inst: BookOption[] = [];
    const pub: BookOption[] = [];
    for (const b of books) {
      if (b.institution_id && b.institution_id === institutionId) {
        inst.push(b);
      } else if (!b.institution_id) {
        pub.push(b);
      }
      // Books from a different institute (institution_id !== this one)
      // shouldn't reach the frontend in the first place, but if they do
      // they're silently ignored.
    }
    return { instituteBooks: inst, publicBooks: pub };
  }, [books, institutionId]);

  // Object lookup beats nested ternaries — adding a third tab later only
  // means adding one map entry, not re-reading the conditional.
  const tabBooks: Record<Tab, BookOption[]> = {
    institute: instituteBooks,
    public: publicBooks,
  };
  const activeBooks = showTabs ? tabBooks[activeTab] : books;

  const filtered = activeBooks.filter(
    (b) =>
      b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      b.author.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const isPublicTabLocked = showTabs && activeTab === 'public' && !ALLOW_PUBLIC_BOOKS;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className={`w-full max-w-lg mx-4 rounded-xl flex flex-col max-h-[80vh] ${
          isDarkMode ? 'bg-slate-800' : 'bg-white'
        }`}
      >
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="min-w-0">
              <h3
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {mode === 'individual' ? 'Assign Book to Student' : 'Assign Book'}
              </h3>
              {targetLabel && (
                <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {targetLabel}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              // hover:text-gray-300 alone is invisible on a white modal
              // (gray-300 < gray-400 in contrast), so explicitly pick a
              // darker hover color in light mode.
              className={`text-gray-400 ${
                isDarkMode ? 'hover:text-gray-300' : 'hover:text-gray-600'
              }`}
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {showTabs && (
            <div
              className={`flex gap-1 mb-4 p-1 rounded-lg ${
                isDarkMode ? 'bg-slate-900/50' : 'bg-gray-100'
              }`}
              role="tablist"
            >
              <TabButton
                active={activeTab === 'institute'}
                onClick={() => setActiveTab('institute')}
                isDarkMode={isDarkMode}
                icon={<BuildingLibraryIcon className="h-4 w-4" />}
                label="Our Books"
                count={instituteBooks.length}
              />
              <TabButton
                active={activeTab === 'public'}
                onClick={() => setActiveTab('public')}
                isDarkMode={isDarkMode}
                icon={<GlobeAltIcon className="h-4 w-4" />}
                label="Public Catalog"
                count={publicBooks.length}
              />
            </div>
          )}

          {isPublicTabLocked && (
            <div
              id="public-catalog-locked-banner"
              role="status"
              className={`mb-4 p-3 rounded-lg flex items-start gap-2.5 text-sm border ${
                isDarkMode
                  ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                  : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
            >
              <LockClosedIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Public catalog is read-only for now.</p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'}`}>
                  Browsing only — public textbooks can't be assigned to your groups
                  yet. Upload a copy under <span className="font-medium">Our Books</span>{' '}
                  to assign it instead.
                </p>
              </div>
            </div>
          )}

          <div className="relative">
            <MagnifyingGlassIcon
              className={`absolute left-3 top-2.5 h-5 w-5 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-400'
              }`}
            />
            <input
              type="text"
              placeholder="Search books..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                isDarkMode
                  ? 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              isDarkMode={isDarkMode}
              activeTab={activeTab}
              hasSearch={Boolean(searchQuery)}
              showTabs={showTabs}
            />
          ) : (
            <div className="space-y-2">
              {filtered.map((book) => {
                const locked = isPublicTabLocked;
                const isAssigningThis = assigning === book.id;
                return (
                  <button
                    key={book.id}
                    onClick={() => !locked && handleAssign(book.id)}
                    // Don't `disabled` locked rows — keyboard users would
                    // skip them entirely and never discover the lock reason.
                    // Block the click in the handler instead and use
                    // `aria-disabled` + `aria-describedby` so screen readers
                    // surface the banner's explanation when focus lands here.
                    disabled={isAssigningThis}
                    aria-disabled={locked || undefined}
                    aria-describedby={locked ? 'public-catalog-locked-banner' : undefined}
                    title={
                      locked
                        ? "Public books can't be assigned to your groups yet."
                        : undefined
                    }
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      locked
                        ? 'cursor-not-allowed opacity-60'
                        : isDarkMode
                        ? 'hover:bg-slate-700/50'
                        : 'hover:bg-gray-50'
                    } ${isAssigningThis ? 'opacity-50' : ''}`}
                  >
                    {book.cover_url ? (
                      <img
                        src={book.cover_url}
                        alt={book.title}
                        className="h-12 w-9 rounded object-cover"
                      />
                    ) : (
                      <div
                        className={`h-12 w-9 rounded flex items-center justify-center ${
                          isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
                        }`}
                      >
                        <BookOpenIcon className="h-4 w-4 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div
                        className={`font-medium truncate ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {book.title}
                      </div>
                      <div
                        className={`text-sm truncate ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        {book.author}
                      </div>
                    </div>
                    {locked ? (
                      <LockClosedIcon
                        className={`h-4 w-4 flex-shrink-0 ${
                          isDarkMode ? 'text-amber-400/70' : 'text-amber-600'
                        }`}
                      />
                    ) : isAssigningThis ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Small subcomponent for the tab strip — keeps the parent's render lean
// and lets the tab button carry consistent styling for active/inactive
// states without sprawling Tailwind chains in the JSX above.
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  isDarkMode: boolean;
  icon: React.ReactNode;
  label: string;
  count: number;
}

const TabButton: React.FC<TabButtonProps> = ({
  active,
  onClick,
  isDarkMode,
  icon,
  label,
  count,
}) => {
  const base =
    'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
  const activeStyles = isDarkMode
    ? 'bg-slate-700 text-white shadow-sm'
    : 'bg-white text-gray-900 shadow-sm';
  const inactiveStyles = isDarkMode
    ? 'text-gray-400 hover:text-gray-200'
    : 'text-gray-500 hover:text-gray-700';

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`${base} ${active ? activeStyles : inactiveStyles}`}
    >
      {icon}
      <span>{label}</span>
      <span
        className={`ml-1 px-1.5 py-0.5 rounded text-xs font-semibold ${
          active
            ? isDarkMode
              ? 'bg-slate-900/60 text-cyan-300'
              : 'bg-gray-100 text-cyan-700'
            : isDarkMode
            ? 'bg-slate-800 text-gray-500'
            : 'bg-gray-200 text-gray-500'
        }`}
      >
        {count}
      </span>
    </button>
  );
};

interface EmptyStateProps {
  isDarkMode: boolean;
  activeTab: Tab;
  hasSearch: boolean;
  showTabs: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  isDarkMode,
  activeTab,
  hasSearch,
  showTabs,
}) => {
  if (hasSearch) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No books match your search.
      </div>
    );
  }

  if (!showTabs) {
    return (
      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        No books available to assign.
      </div>
    );
  }

  if (activeTab === 'public') {
    return (
      <div className={`text-center py-8 px-4 space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        <p>The public catalog is empty right now.</p>
        <p className="text-xs">
          Canonical platform textbooks (NCERT, OpenStax, etc.) appear here once
          they're approved by a platform admin.
        </p>
      </div>
    );
  }

  return (
    <div className={`text-center py-8 px-4 space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
      <p>No books your institute owns yet.</p>
      <p className="text-xs">
        Books your institute uploads appear here once approved by a platform admin.
        Check <span className="font-medium">My Requests</span> for status.
      </p>
    </div>
  );
};

export default AssignBookModal;
